import React, { useEffect, useRef } from 'react';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { StyledAddressMap, StyledMapsLink, StyledApproxNote } from './styles';

const MAP_STYLE_URL =
  process.env.REACT_APP_MAP_STYLE_URL ||
  'https://tiles.openfreemap.org/styles/positron';

const RTL_PLUGIN_URL =
  process.env.REACT_APP_MAP_RTL_PLUGIN_URL ||
  'https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.min.js';

// Arabic/Hebrew place labels need the RTL text plugin for correct letter
// shaping and bidi ordering — without it Arabic renders disconnected and
// reversed (e.g. "بغداد" → "دادغب"). Registered once, globally, and lazily.
if (
  typeof maplibregl.getRTLTextPluginStatus === 'function' &&
  maplibregl.getRTLTextPluginStatus() === 'unavailable'
) {
  maplibregl.setRTLTextPlugin(RTL_PLUGIN_URL, () => undefined, true);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface AddressMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  /** Camera tilt in degrees for the 3D perspective (0 = flat top-down). */
  pitch?: number;
  /** Camera rotation in degrees. */
  bearing?: number;
  /** Optional "Open in Maps" external link overlaid on the map. */
  externalUrl?: string;
  externalLabel?: string;
  /** When true, render a soft area halo (not an exact pin) — used when only
   *  the governorate is known, not precise coordinates. */
  approximate?: boolean;
  /** Caption pill shown at the bottom (e.g. "Approximate area · Basra"). */
  caption?: string;
  className?: string;
}

// Brand-ink teardrop pin with a lime center. Built via DOM APIs (not
// innerHTML); SVG presentation attributes can't read CSS vars, so the
// token hex values are inlined.
const createPinElement = (): HTMLDivElement => {
  const el = document.createElement('div');
  el.style.cssText =
    'width:30px;height:38px;cursor:default;filter:drop-shadow(0 4px 6px rgba(9,15,56,.28));';

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '30');
  svg.setAttribute('height', '38');
  svg.setAttribute('viewBox', '0 0 30 38');
  svg.setAttribute('fill', 'none');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute(
    'd',
    'M15 1.5C7.8 1.5 2 7.2 2 14.3c0 9.2 11.3 21 12.1 21.8a1.3 1.3 0 0 0 1.8 0C16.7 35.3 28 23.5 28 14.3 28 7.2 22.2 1.5 15 1.5Z'
  );
  path.setAttribute('fill', '#090F38');
  path.setAttribute('stroke', '#FFFFFF');
  path.setAttribute('stroke-width', '2.2');

  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', '15');
  circle.setAttribute('cy', '14.3');
  circle.setAttribute('r', '4.6');
  circle.setAttribute('fill', '#C5E861');

  svg.appendChild(path);
  svg.appendChild(circle);
  el.appendChild(svg);
  return el;
};

// Soft area halo for approximate (governorate-only) locations: a translucent
// brand-ink disc with a solid centre dot. Signals "around here", not an exact
// point. Anchored at centre.
const createHaloElement = (): HTMLDivElement => {
  const el = document.createElement('div');
  el.style.cssText =
    'width:74px;height:74px;border-radius:50%;cursor:default;' +
    'background:radial-gradient(circle,rgba(9,15,56,.22) 0%,rgba(9,15,56,.10) 55%,rgba(9,15,56,0) 72%);' +
    'display:flex;align-items:center;justify-content:center;';

  const dot = document.createElement('div');
  dot.style.cssText =
    'width:14px;height:14px;border-radius:50%;background:#090F38;' +
    'border:2.5px solid #FFFFFF;box-shadow:0 2px 5px rgba(9,15,56,.35);';
  el.appendChild(dot);
  return el;
};

const AddressMap: React.FC<AddressMapProps> = ({
  latitude,
  longitude,
  zoom = 14,
  pitch = 55,
  bearing = -18,
  externalUrl,
  externalLabel,
  approximate = false,
  caption,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [longitude, latitude],
      zoom,
      pitch, // tilt the camera for the 3D perspective
      bearing,
      scrollZoom: false, // don't hijack page scroll inside the card
      attributionControl: false, // add a compact one below instead
    });
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }),
      'top-right'
    );
    // OSM data is ODbL — keep attribution, but collapsed to a small "i" icon
    // so it doesn't crowd the card or overlap the caption.
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.on('load', () => {
      map.resize();
      // Compact attribution starts expanded; collapse it to just the "i" icon.
      // MapLibre keeps a mapbox-prefixed duplicate class for back-compat, so
      // remove both or the inner text stays visible.
      containerRef.current
        ?.querySelector('.maplibregl-ctrl-attrib')
        ?.classList.remove('maplibregl-compact-show', 'mapboxgl-compact-show');

      // Extrude building footprints for the 3D view. The openmaptiles vector
      // source carries render_height / render_min_height; hide_3d marks
      // buildings that shouldn't be raised. Inserted below the first label
      // layer so place names stay on top.
      if (!map.getLayer('3d-buildings') && map.getSource('openmaptiles')) {
        map.addLayer(
          {
            id: '3d-buildings',
            source: 'openmaptiles',
            'source-layer': 'building',
            type: 'fill-extrusion',
            minzoom: 14,
            filter: ['!=', ['get', 'hide_3d'], true],
            paint: {
              'fill-extrusion-color': [
                'interpolate',
                ['linear'],
                ['coalesce', ['get', 'render_height'], 0],
                0, '#e7e9ef',
                40, '#d4d8e1',
                120, '#bcc2d0',
              ],
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              'fill-extrusion-height': [
                'interpolate', ['linear'], ['zoom'],
                14, 0,
                15, ['coalesce', ['get', 'render_height'], 0],
              ] as any,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              'fill-extrusion-base': [
                'interpolate', ['linear'], ['zoom'],
                14, 0,
                15, ['coalesce', ['get', 'render_min_height'], 0],
              ] as any,
              'fill-extrusion-opacity': 0.9,
            },
          },
          'waterway_line_label'
        );
      }
    });

    markerRef.current = new maplibregl.Marker(
      approximate
        ? { element: createHaloElement(), anchor: 'center' }
        : { element: createPinElement(), anchor: 'bottom' }
    )
      .setLngLat([longitude, latitude])
      .addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Initialised once; coordinate updates handled in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setCenter([longitude, latitude]);
    markerRef.current?.setLngLat([longitude, latitude]);
  }, [latitude, longitude]);

  return (
    <StyledAddressMap ref={containerRef} className={className}>
      {externalUrl && (
        <StyledMapsLink href={externalUrl} target='_blank' rel='noreferrer'>
          {externalLabel || 'Open in Maps ↗'}
        </StyledMapsLink>
      )}
      {caption && <StyledApproxNote>{caption}</StyledApproxNote>}
    </StyledAddressMap>
  );
};

export default AddressMap;
